import * as React from "react";
import { makeStyles, shorthands, tokens, mergeClasses } from "@fluentui/react-components";
import { Subtract20Regular, SquareMultiple20Regular, Dismiss20Regular } from "@fluentui/react-icons";
import { BRAND } from "../risikoanalyseStep/tokens";
import { useWindowManager } from "./WindowManagerContext";
import { useResponsive } from "../../hooks/useResponsive";

const useStyles = makeStyles({
  root: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    flexShrink: 0,
  },
  btn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("8px"),
    transitionProperty: "background-color, color",
    transitionDuration: "120ms",
    ":hover": { backgroundColor: BRAND.white },
  },
  /**
   * Icon-Variante: quadratisch, ohne Text. Sie sitzt in der Kontextzeile des
   * Immobilien-Formulars, wo die gestrichene Kopfzeile keinen Platz mehr für
   * beschriftete Knöpfe lässt — der Klartext steckt in `title`/`aria-label`.
   */
  btnIcon: {
    justifyContent: "center",
    width: "32px",
    height: "32px",
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    columnGap: 0,
  },
  btnPhone: {
    // Touch-Target ≥44px, wie im Fenster-Chrome und im Dock.
    minWidth: "44px",
    minHeight: "44px",
  },
  /** Schließen ist die einzige destruktive Aktion — Hover wie im Fenster-Chrome. */
  btnClose: {
    ":hover": {
      backgroundColor: "#a1331c",
      color: BRAND.white,
    },
  },
});

type Props = {
  /** ID der Fenster-Instanz, in der die Seite läuft. Ohne ID: nichts rendern. */
  windowId?: string;
  /**
   * `"text"` (Default) — beschriftete Knöpfe für die Formular-Kopfzeile.
   * `"icons"` — reine Icon-Knöpfe für die Kontextzeile des Immobilien-Formulars.
   */
  variant?: "text" | "icons";
  /**
   * „Schließen". Nur gerendert, wenn gesetzt — der Aufrufer entscheidet, ob es
   * einen Schließweg gibt, und liefert dieselbe Logik wie der bisherige
   * „Zurück"-Knopf (inkl. Rückfrage bei ungespeicherten Eingaben).
   */
  onClose?: () => void;
};

/**
 * Die Bedienelemente der Vollseite: „Als Fenster" (überführt dieselbe Instanz
 * in ein bewegliches Fenster mit Titelleiste), „Minimieren" (legt sie als Chip
 * ins Dock) und optional „Schließen".
 *
 * Rendert `null`, solange das Fenster nicht existiert oder nicht im Zustand
 * `"page"` ist — im Fenster-Modus übernimmt die Titelleiste die Bedienung.
 *
 * Achtung: die Komponente liest den WindowManager-Context und darf deshalb nur
 * dort gerendert werden, wo es einen Provider gibt. Die öffentlichen Formulare
 * (Kundenstrecke) laufen ohne Provider — dort bleibt sie ungerendert, weil sie
 * keine `windowId` haben.
 */
const PageModeControls: React.FC<Props> = ({ windowId, variant = "text", onClose }) => {
  const styles = useStyles();
  const { isPhoneOrTablet } = useResponsive();
  const { windows, setWindowMode, minimizeWindow } = useWindowManager();

  const win = windowId ? windows.find((w) => w.id === windowId) : undefined;
  // Im eigenen Browserfenster bringt dessen Kopfzeile die Knöpfe mit.
  if (!win || win.state !== "page" || win.browserFenster) return null;

  const nurIcons = variant === "icons";
  const btnClass = mergeClasses(
    styles.btn,
    nurIcons && styles.btnIcon,
    isPhoneOrTablet && styles.btnPhone,
  );

  return (
    <span className={styles.root}>
      {/* Auf Phone/Tablet gibt es keine schwebenden Fenster (WindowLayer
          rendert dort alles vollflächig) — der Knopf entfällt, analog zum dort
          schon versteckten „Maximieren". */}
      {!isPhoneOrTablet && (
        <button
          type="button"
          className={btnClass}
          onClick={() => setWindowMode(win.id, "normal")}
          title="Als bewegliches Fenster anzeigen"
          aria-label={nurIcons ? "Als bewegliches Fenster anzeigen" : undefined}
        >
          <SquareMultiple20Regular />
          {!nurIcons && "Als Fenster"}
        </button>
      )}
      <button
        type="button"
        className={btnClass}
        onClick={() => minimizeWindow(win.id)}
        title="Für später minimieren"
        aria-label={nurIcons ? "Für später minimieren" : undefined}
      >
        <Subtract20Regular />
        {!nurIcons && "Minimieren"}
      </button>
      {onClose && (
        <button
          type="button"
          className={mergeClasses(btnClass, styles.btnClose)}
          onClick={onClose}
          title="Formular schließen"
          aria-label={nurIcons ? "Formular schließen" : undefined}
        >
          <Dismiss20Regular />
          {!nurIcons && "Schließen"}
        </button>
      )}
    </span>
  );
};

export default PageModeControls;
